import { useState, useContext, useEffect } from "react";

// State
import { useFinaliseOrder } from "../../state/draftOrderFinaliseState";

// Context
import { AlertNotificationContext } from "../../contexts/AlertNotificationContext";

// Hooks
import { useCheckAPI } from "../../hooks/useCheckAPI";

// Components
import { ShipmentOptionsGroup } from "./ShipmentOptionsGroup";

// Utils
import { getLatestDate } from "../../utils/Utils";
import { currencyFormatter } from "../../utils/Utils";

// MUI Imports
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Paper from "@mui/material/Paper";
import Slider from "@mui/material/Slider";

// Date Functions
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isBetween from "dayjs/plugin/isBetween";
import minMax from "dayjs/plugin/minMax";
import { Typography } from "@mui/material";
dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(minMax);

export const ShipmentOptions = ({
  order,
  shipmentDefault,
  shipmentOne,
  setShipmentOne,
  shipmentTwo,
  setShipmentTwo,
  setOrderFinaliseInfo,
}) => {
  const [orderFinalise, setOrderFinalise] = useState({
    filter: {},
    header: {},
    lines: [],
    staff: [],
    shippingServices: [],
  });
  const { setAlertNotification } = useContext(AlertNotificationContext);

  // Fetch Draft Order Finalise data
  const { isError, error, data, isSuccess } = useFinaliseOrder(
    order.data.order.no,
  );

  const successFunc = () => {
    const finaliseData = data.data.data.data.data;
    setOrderFinalise(finaliseData);
    // Check if any lines have over-entitlement reasons
    const hasOverEntitlementItems =
      finaliseData.lines?.some(
        (line) =>
          line.overEntitlementReason && line.overEntitlementReason !== "",
      ) || false;
    setOrderFinaliseInfo({
      ...finaliseData.header,
      hasOverEntitlementItems,
    });
  };

  const errorFunc = () =>
    setAlertNotification({
      message: `Error: ${data.data.status}: ${data.data.statusText}`,
      severity: "error",
    });

  // Hook to check the BC API for errors
  useCheckAPI(isSuccess, data, data?.data?.status, successFunc, errorFunc);

  // Set State
  const [canDoAvailable, setCanDoAvailable] = useState(false);
  const [canDoCustom, setCanDoCustom] = useState(false);
  const [customSliderValue, setCustomSliderValue] = useState(0);
  const [customSliderMarks, setCustomSliderMarks] = useState([{}]);
  // {"shippingAgentCode":"DX-LIVE","code":"NS","description":"DX Live Next Day Signature","carriageCharge":"7.95","carriageChargeLimit":"150.00"}

  const shippingDefault = {
    index: "",
    agent: "",
    service: "",
    cost: 0,
  };

  // const shipmentDefault = {
  //   lines: [],
  //   subtotal: 0,
  //   total: 0,
  //   date: "",
  //   shipping: shippingDefault,
  // };

  // const [shipmentOne, setShipmentOne] = useState(shipmentDefault);
  // const [shipmentTwo, setShipmentTwo] = useState(shipmentDefault);

  // Shipping Options
  const [option, setOption] = useState("0");

  const handleOptionChange = (event) => {
    setOption(event.target.value);
    if (event.target.value === "0") shipComplete();
    if (event.target.value === "1") shipAvailable();
    if (event.target.value === "2")
      shipCustom(customSliderMarks[customSliderValue].label);
  };

  // Check if we have any lines with an sla less than or equal to 1
  const canDoAvailableNow = (orderLines) => {
    return orderLines.some((x) => Number(x.sla) <= 1);
  };

  // Check if can do custom order split
  const canDoCustomSplit = (orderLines) => {
    // Check if we have more than one order line
    if (orderLines.length > 1) {
      // Create an array with unique dates
      let allDates = orderLines.map((a) => a.sla_date);
      let uniqueDates = [...new Set(allDates)];
      if (uniqueDates.length > 1) {
        // Sort the unique array by date
        const sortedUniqueDates = uniqueDates.sort((a, b) =>
          dayjs(a, "DD/MM/YY").isAfter(dayjs(b, "DD/MM/YY")) ? 1 : -1,
        );
        // Set Custom Marks and value
        const customMarks = sortedUniqueDates.map((item, index) => ({
          value: index,
          label: item,
        }));
        setCustomSliderMarks(customMarks);
        setCustomSliderValue(customMarks[0].value);
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    if (orderFinalise.header?.no) {
      const defaultSplit = orderFinalise.header.skip === "Yes" && true;
      setCanDoCustom(canDoCustomSplit(orderFinalise.lines));
      const availableNow = canDoAvailableNow(orderFinalise.lines);
      setCanDoAvailable(availableNow);
      // If default split is allowed and available now is an option we set it as default shipping option
      if (defaultSplit && availableNow) {
        setOption("1");
        shipAvailable();
      } else {
        setOption("0");
        shipComplete();
      }
    }
  }, [orderFinalise.header?.no, orderFinalise.lines]);

  // Ship Complete Order
  const shipComplete = () => {
    if (orderFinalise.lines.length > 0) {
      const shipment1 = orderFinalise.lines;
      setShipmentOne({
        lines: shipment1,
        date: getLatestDate(shipment1),
        subtotal: subtotalCalc(shipment1),
        total: 0,
        shipping: shippingDefault,
      });
      setShipmentTwo(shipmentDefault);
    }
  };

  // Ship All Available Now
  const shipAvailable = () => {
    if (orderFinalise.lines.length > 0) {
      const shipment1 = orderFinalise.lines.filter((x) => x.sla <= 1);
      const shipment2 = orderFinalise.lines.filter((x) => x.sla > 1);
      setShipmentOne({
        lines: shipment1,
        date: getLatestDate(shipment1),
        subtotal: subtotalCalc(shipment1),
        total: 0,
        shipping: shippingDefault,
      });
      setShipmentTwo({
        lines: shipment2,
        date: getLatestDate(shipment2),
        subtotal: subtotalCalc(shipment2),
        total: 0,
        shipping: shippingDefault,
      });
    }
  };

  // Custom Order Split based on specified date
  const shipCustom = (splitDate) => {
    // Split the order into two using the splitDate
    const shipment1 = orderFinalise.lines.filter(
      (x) => x.sla_date <= splitDate,
    );
    const shipment2 = orderFinalise.lines.filter((x) => x.sla_date > splitDate);
    setShipmentOne({
      lines: shipment1,
      date: getLatestDate(shipment1),
      subtotal: subtotalCalc(shipment1),
      total: 0,
      shipping: shippingDefault,
    });
    setShipmentTwo({
      lines: shipment2,
      date: getLatestDate(shipment2),
      subtotal: subtotalCalc(shipment2),
      total: 0,
      shipping: shippingDefault,
    });
  };

  // Handle Slider Change
  const handleSliderChange = (event, newValue) => {
    setCustomSliderValue(newValue);
    shipCustom(customSliderMarks[newValue].label);
  };

  const subtotalCalc = (shipmentLines) => {
    let subtotal = 0;
    shipmentLines?.map((line) => {
      subtotal += Number(line.quantity) * Number(line.cost);
    });
    return subtotal;
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Stack spacing={2}>
        <FormControl>
          <FormLabel id="shipment-options-buttons-group">
            Shipping Options
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby="shipment-options-buttons-group"
            name="shipment-options-buttons-group"
            value={option}
            onChange={handleOptionChange}
          >
            <FormControlLabel
              value={"0"}
              control={<Radio />}
              label="Complete Order"
            />
            <FormControlLabel
              value={"1"}
              control={<Radio />}
              label="Available Now"
              disabled={!canDoAvailable}
            />
            <FormControlLabel
              value={"2"}
              control={<Radio />}
              label="Custom Split"
              disabled={!canDoCustom}
            />
          </RadioGroup>
        </FormControl>

        {option === "0" && (
          <Typography>All items will be shipped together.</Typography>
        )}
        {option === "1" && (
          <Typography>
            Currently in-stock items will be despatched immediately, the
            remaining items will be shipped separately. Each shipment will be
            converted into a separate order.
          </Typography>
        )}
        {option === "2" && (
          <Box>
            <Typography>
              Items that are available within the date specified by the slider
              will be shipped first, any remaining items will be shipped after.
              Each shipment will be converted into a separate order.
            </Typography>
            <Box sx={{ mt: 3, mx: 5 }}>
              <Slider
                aria-label="Custom Date Selection"
                value={customSliderValue}
                marks={customSliderMarks}
                min={0}
                max={customSliderMarks[customSliderMarks.length - 1].value}
                onChange={handleSliderChange}
              />
            </Box>
          </Box>
        )}

        {/* Shipment One */}
        {shipmentOne.lines.length > 0 && (
          <ShipmentOptionsGroup
            shipmentNo={1}
            staff={orderFinalise.staff}
            shippingServices={orderFinalise.shippingServices}
            shipment={shipmentOne}
            setShipment={setShipmentOne}
          />
        )}

        {/* Shipment Two */}
        {shipmentTwo.lines.length > 0 && (
          <ShipmentOptionsGroup
            shipmentNo={2}
            staff={orderFinalise.staff}
            shippingServices={orderFinalise.shippingServices}
            shipment={shipmentTwo}
            setShipment={setShipmentTwo}
          />
        )}

        <Paper
          elevation={2}
          sx={{ p: 2, display: "flex", flexDirection: "row-reverse" }}
        >
          <Typography sx={{ fontWeight: "bold", pl: 2 }}>
            Total:{" "}
            {currencyFormatter("GB", shipmentOne.total + shipmentTwo.total)}
          </Typography>
          <Typography sx={{ fontWeight: "bold", pl: 2 }}>
            Shipping:{" "}
            {currencyFormatter(
              "GB",
              shipmentOne.shipping.cost + shipmentTwo.shipping.cost,
            )}
          </Typography>
          {shipmentTwo.lines.length > 0 && (
            <Typography sx={{ fontWeight: "bold", pl: 2 }}>
              Shipment Two: {currencyFormatter("GB", shipmentTwo.total)}
            </Typography>
          )}
          <Typography sx={{ fontWeight: "bold", pl: 2 }}>
            Shipment One: {currencyFormatter("GB", shipmentOne.total)}
          </Typography>
        </Paper>
      </Stack>
    </Box>
  );
};
